import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./config";
import {
  Shop,
  ShopFormData,
  CoverageRequirement,
  WeeklyCoverage,
} from "../../types/shop.types";
import { DayHours, WeeklyHours, DayOfWeek } from "../../types/common.types";
import { getNextShopColor } from "../../utils/colorUtils";
import { toFirestoreDateKey } from "../utils/dateUtils";

/**
 * Firebase Shops Service
 * Handles shop CRUD operations for organizations
 */
export const shopsService = {
  /**
   * Create a new shop for an organization
   */
  async createShop(
    organizationId: string,
    data: ShopFormData
  ): Promise<string> {
    try {
      // Get existing shops to determine color
      const existingShops = await this.getShops(organizationId);
      const existingColors = existingShops
        .map(shop => shop.color)
        .filter((color): color is string => !!color);
      const color = getNextShopColor(existingColors);

      const shopRef = doc(
        collection(db, "organizations", organizationId, "shops")
      );
      const shopId = shopRef.id;

      // Initialize empty coverage requirements for each day
      const emptyCoverage: WeeklyCoverage = {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      };

      await setDoc(shopRef, {
        name: data.name,
        address: data.address || "",
        contactInfo: data.contactInfo || {},
        color,
        hoursConfig: {}, // Empty initially, will be populated with specific date overrides
        defaultHours: data.defaultHours,
        defaultCoverage: emptyCoverage,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return shopId;
    } catch (error) {
      console.error("Create shop error:", error);
      throw error;
    }
  },

  /**
   * Get a shop by ID
   */
  async getShop(organizationId: string, shopId: string): Promise<Shop | null> {
    try {
      const shopDoc = await getDoc(
        doc(db, "organizations", organizationId, "shops", shopId)
      );

      if (shopDoc.exists()) {
        return {
          id: shopDoc.id,
          ...shopDoc.data(),
        } as Shop;
      }

      return null;
    } catch (error) {
      console.error("Get shop error:", error);
      throw error;
    }
  },

  /**
   * Get all shops for an organization
   */
  async getShops(organizationId: string): Promise<Shop[]> {
    try {
      const shopsRef = collection(db, "organizations", organizationId, "shops");
      const q = query(shopsRef, orderBy("name", "asc"));
      const querySnapshot = await getDocs(q);

      const shops: Shop[] = [];
      querySnapshot.forEach((doc) => {
        shops.push({
          id: doc.id,
          ...doc.data(),
        } as Shop);
      });

      return shops;
    } catch (error) {
      console.error("Get shops error:", error);
      throw error;
    }
  },

  /**
   * Update a shop
   */
  async updateShop(
    organizationId: string,
    shopId: string,
    data: Partial<ShopFormData>
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      await updateDoc(shopRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Update shop error:", error);
      throw error;
    }
  },

  /**
   * Delete a shop
   */
  async deleteShop(organizationId: string, shopId: string): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      await deleteDoc(shopRef);
    } catch (error) {
      console.error("Delete shop error:", error);
      throw error;
    }
  },

  /**
   * Update default hours for a shop
   */
  async updateDefaultHours(
    organizationId: string,
    shopId: string,
    defaultHours: WeeklyHours
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      await updateDoc(shopRef, {
        defaultHours,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Update default hours error:", error);
      throw error;
    }
  },

  /**
   * Update hours for a specific date
   * @param organizationId - Organization ID
   * @param shopId - Shop ID
   * @param date - Date in YYYY-MM-DD format
   * @param hours - Hours configuration for that date
   */
  async updateDateHours(
    organizationId: string,
    shopId: string,
    date: string,
    hours: DayHours
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      await updateDoc(shopRef, {
        [`hoursConfig.${date}`]: hours,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Update date hours error:", error);
      throw error;
    }
  },

  /**
   * Remove hours override for a specific date (reverts to default)
   */
  async removeDateHours(
    organizationId: string,
    shopId: string,
    date: string
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      const shopDoc = await getDoc(shopRef);

      if (shopDoc.exists()) {
        const data = shopDoc.data();
        const hoursConfig = { ...data.hoursConfig };
        delete hoursConfig[date];

        await updateDoc(shopRef, {
          hoursConfig,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Remove date hours error:", error);
      throw error;
    }
  },

  /**
   * Get hours for a specific date
   * Returns specific date hours if available, otherwise returns default for that day of week
   */
  async getHoursForDate(
    organizationId: string,
    shopId: string,
    date: Date
  ): Promise<DayHours> {
    try {
      const shop = await this.getShop(organizationId, shopId);

      if (!shop) {
        throw new Error("Shop not found");
      }

      // Format date as YYYY-MM-DD (using local timezone)
      const dateKey = toFirestoreDateKey(date);

      // Check if there's a specific override for this date
      if (shop.hoursConfig[dateKey]) {
        return shop.hoursConfig[dateKey];
      }

      // Return default hours for this day of week
      const days = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayName = days[date.getDay()] as keyof WeeklyHours;

      return shop.defaultHours[dayName];
    } catch (error) {
      console.error("Get hours for date error:", error);
      throw error;
    }
  },

  /**
   * Update default coverage requirements for all days
   */
  async updateDefaultCoverage(
    organizationId: string,
    shopId: string,
    defaultCoverage: WeeklyCoverage
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      await updateDoc(shopRef, {
        defaultCoverage,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Update default coverage error:", error);
      throw error;
    }
  },

  /**
   * Add a coverage requirement to a specific day
   */
  async addCoverageToDay(
    organizationId: string,
    shopId: string,
    day: DayOfWeek,
    coverage: CoverageRequirement
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const defaultCoverage = shop.defaultCoverage || ({} as WeeklyCoverage);
      const dayCoverage = defaultCoverage[day] || [];

      const updatedCoverage = {
        ...defaultCoverage,
        [day]: [...dayCoverage, coverage],
      };

      await this.updateDefaultCoverage(organizationId, shopId, updatedCoverage);
    } catch (error) {
      console.error("Add coverage to day error:", error);
      throw error;
    }
  },

  /**
   * Remove a coverage requirement from a specific day
   */
  async removeCoverageFromDay(
    organizationId: string,
    shopId: string,
    day: DayOfWeek,
    coverageId: string
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const defaultCoverage = shop.defaultCoverage || ({} as WeeklyCoverage);
      const dayCoverage = defaultCoverage[day] || [];

      const updatedCoverage = {
        ...defaultCoverage,
        [day]: dayCoverage.filter((c) => c.id !== coverageId),
      };

      await this.updateDefaultCoverage(organizationId, shopId, updatedCoverage);
    } catch (error) {
      console.error("Remove coverage from day error:", error);
      throw error;
    }
  },

  /**
   * Update a specific coverage requirement
   */
  async updateCoverage(
    organizationId: string,
    shopId: string,
    day: DayOfWeek,
    coverageId: string,
    updates: Partial<Omit<CoverageRequirement, "id">>
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const defaultCoverage = shop.defaultCoverage || ({} as WeeklyCoverage);
      const dayCoverage = defaultCoverage[day] || [];

      const updatedCoverage = {
        ...defaultCoverage,
        [day]: dayCoverage.map((c) =>
          c.id === coverageId ? { ...c, ...updates } : c
        ),
      };

      await this.updateDefaultCoverage(organizationId, shopId, updatedCoverage);
    } catch (error) {
      console.error("Update coverage error:", error);
      throw error;
    }
  },

  /**
   * Move a coverage requirement from one day to another
   */
  async moveCoverage(
    organizationId: string,
    shopId: string,
    fromDay: DayOfWeek,
    toDay: DayOfWeek,
    coverageId: string
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const defaultCoverage = shop.defaultCoverage || ({} as WeeklyCoverage);
      const fromCoverage = defaultCoverage[fromDay] || [];
      const toCoverage = defaultCoverage[toDay] || [];

      const coverage = fromCoverage.find((c) => c.id === coverageId);
      if (!coverage) throw new Error("Coverage not found");

      const updatedCoverage = {
        ...defaultCoverage,
        [fromDay]: fromCoverage.filter((c) => c.id !== coverageId),
        [toDay]: [...toCoverage, coverage],
      };

      await this.updateDefaultCoverage(organizationId, shopId, updatedCoverage);
    } catch (error) {
      console.error("Move coverage error:", error);
      throw error;
    }
  },

  /**
   * Copy a coverage requirement from one day to another
   */
  async copyCoverage(
    organizationId: string,
    shopId: string,
    fromDay: DayOfWeek,
    toDay: DayOfWeek,
    coverageId: string
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const defaultCoverage = shop.defaultCoverage || ({} as WeeklyCoverage);
      const fromCoverage = defaultCoverage[fromDay] || [];
      const toCoverage = defaultCoverage[toDay] || [];

      const coverage = fromCoverage.find((c) => c.id === coverageId);
      if (!coverage) throw new Error("Coverage not found");

      // Create a copy with new ID
      const copiedCoverage = {
        ...coverage,
        id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      };

      const updatedCoverage = {
        ...defaultCoverage,
        [toDay]: [...toCoverage, copiedCoverage],
      };

      await this.updateDefaultCoverage(organizationId, shopId, updatedCoverage);
    } catch (error) {
      console.error("Copy coverage error:", error);
      throw error;
    }
  },

  /**
   * Copy all coverage to next week
   */
  async copyCoverageToNextWeek(
    organizationId: string,
    shopId: string
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop || !shop.defaultCoverage) {
        throw new Error("Shop or coverage not found");
      }

      // Default coverage are already weekly templates, so this is a no-op
      // But we can use this to copy to date-specific coverage in the future
      console.log(
        "Default coverage are weekly templates, already apply to all weeks"
      );
    } catch (error) {
      console.error("Copy coverage to next week error:", error);
      throw error;
    }
  },

  /**
   * Get coverage requirements for a specific date
   * Returns date-specific coverage if available, otherwise returns default coverage for that day
   */
  getCoverageForDate(shop: Shop, date: Date): CoverageRequirement[] {
    const dateKey = toFirestoreDateKey(date);

    // Check if there's a specific override for this date
    if (shop.dateCoverage && shop.dateCoverage[dateKey]) {
      return shop.dateCoverage[dateKey];
    }

    // Return default coverage for this day of week
    const days: DayOfWeek[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const dayName = days[date.getDay()];

    return shop.defaultCoverage?.[dayName] || [];
  },

  /**
   * Update coverage requirements for a specific date
   * Note: An empty array explicitly indicates "closed/no coverage" for that date,
   * which is different from no override (which would use default coverage)
   */
  async updateDateCoverage(
    organizationId: string,
    shopId: string,
    date: string,
    coverage: CoverageRequirement[]
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);

      // Save the coverage array (even if empty - empty array means explicitly closed)
      await updateDoc(shopRef, {
        [`dateCoverage.${date}`]: coverage,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Update date coverage error:", error);
      throw error;
    }
  },

  /**
   * Add a coverage requirement to a specific date
   */
  async addCoverageToDate(
    organizationId: string,
    shopId: string,
    date: string,
    coverage: CoverageRequirement
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const dateKey = date;
      const currentDateCoverage = shop.dateCoverage?.[dateKey] || [];

      await this.updateDateCoverage(organizationId, shopId, dateKey, [
        ...currentDateCoverage,
        coverage,
      ]);
    } catch (error) {
      console.error("Add coverage to date error:", error);
      throw error;
    }
  },

  /**
   * Remove a coverage requirement from a specific date
   */
  async removeCoverageFromDate(
    organizationId: string,
    shopId: string,
    date: string,
    coverageId: string
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const dateKey = date;
      const currentDateCoverage = shop.dateCoverage?.[dateKey] || [];

      const updatedCoverage = currentDateCoverage.filter((c) => c.id !== coverageId);

      await this.updateDateCoverage(organizationId, shopId, dateKey, updatedCoverage);
    } catch (error) {
      console.error("Remove coverage from date error:", error);
      throw error;
    }
  },

  /**
   * Update a specific coverage requirement on a specific date
   */
  async updateCoverageOnDate(
    organizationId: string,
    shopId: string,
    date: string,
    coverageId: string,
    updates: Partial<Omit<CoverageRequirement, "id">>
  ): Promise<void> {
    try {
      const shop = await this.getShop(organizationId, shopId);
      if (!shop) throw new Error("Shop not found");

      const dateKey = date;
      const currentDateCoverage = shop.dateCoverage?.[dateKey] || [];

      const updatedCoverage = currentDateCoverage.map((c) =>
        c.id === coverageId ? { ...c, ...updates } : c
      );

      await this.updateDateCoverage(organizationId, shopId, dateKey, updatedCoverage);
    } catch (error) {
      console.error("Update coverage on date error:", error);
      throw error;
    }
  },

  /**
   * Remove date-specific coverage override (reverts to default)
   */
  async removeDateCoverage(
    organizationId: string,
    shopId: string,
    date: string
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);
      const shopDoc = await getDoc(shopRef);

      if (shopDoc.exists()) {
        const data = shopDoc.data();
        const dateCoverage = { ...data.dateCoverage };
        delete dateCoverage[date];

        await updateDoc(shopRef, {
          dateCoverage,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Remove date coverage error:", error);
      throw error;
    }
  },

  /**
   * Batch update date coverage for a shop - updates multiple dates in a single API call
   * @param updates - Record of date keys to coverage arrays, or "delete" to remove the entry
   */
  async batchUpdateShopCoverage(
    organizationId: string,
    shopId: string,
    updates: Record<string, CoverageRequirement[] | "delete">
  ): Promise<void> {
    try {
      const shopRef = doc(db, "organizations", organizationId, "shops", shopId);

      // Build the update object with dot notation for nested fields
      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };

      for (const [dateKey, coverage] of Object.entries(updates)) {
        if (coverage === "delete") {
          // Use deleteField() to remove this date's entry
          updateData[`dateCoverage.${dateKey}`] = deleteField();
        } else {
          // Set the coverage array for this date
          updateData[`dateCoverage.${dateKey}`] = coverage;
        }
      }

      await updateDoc(shopRef, updateData);
    } catch (error) {
      console.error("Batch update shop coverage error:", error);
      throw error;
    }
  },
};
